import { DropdownOptions } from '../../../components/dropdown/Dropdown';
import {
    Category,
    Department,
    FormProps,
    RelatedForm,
    Tag,
} from '../types/FormTypes';

export const getDepartments = (forms: FormProps[]) => {
    try {
        const departments: Department[] = [];

        for (const form of forms) {
            const departmentName = form?.Department__c;

            const departmentAlreadyDetected = departments?.find(
                ({ name }) => name === departmentName,
            );

            if (!departmentAlreadyDetected) {
                departments?.push({ name: departmentName });
            }
        }

        return departments;
    } catch {
        return [];
    }
};

export const getCategories = (forms: FormProps[]) => {
    try {
        const categories: Category[] = [];

        for (const form of forms) {
            const categoryName = form?.Category__c;

            const categoryAlreadyDetected = categories?.find(
                ({ name }) => name === categoryName,
            );

            if (!categoryAlreadyDetected) {
                categories?.push({ name: categoryName, amountOfForms: 1 });
            } else {
                const indexOfCategory = categories?.findIndex(
                    ({ name }) => name === categoryName,
                );

                categories[indexOfCategory].amountOfForms++;
            }
        }

        return categories;
    } catch {
        return [];
    }
};

export const getTags = (form: FormProps) => {
    try {
        const tags = form?.Tags__c?.split(',');

        const tagsCleaned: Tag[] = [];
        for (const tag of tags) {
            const tagCleaned = tag?.toLowerCase()?.trim();

            tagsCleaned.push({ name: tagCleaned });
        }

        return tagsCleaned;
    } catch {
        return [];
    }
};

export const getMatchingTagCount = (
    formTags: Tag[],
    selectedFormTags: Tag[],
) => {
    try {
        let matchingTagCount: number = 0;
        for (const tag of formTags) {
            const matchingTagDetected = selectedFormTags.find(
                ({ name }) => name === tag?.name,
            );

            if (matchingTagDetected) {
                matchingTagCount++;
            }
        }

        return matchingTagCount;
    } catch {
        return 0;
    }
};

export const getRelatedFormByMatchingTags = (
    relatedForms: RelatedForm[],
    form: FormProps,
    selectedForm: FormProps,
    matchingTagCount: number,
) => {
    try {
        if (matchingTagCount > 0) {
            const relatedFormAlreadyDetected = relatedForms?.find(
                ({ Id }) => Id === form?.Id,
            );

            if (!relatedFormAlreadyDetected && form?.Id !== selectedForm?.Id) {
                relatedForms?.push({
                    Id: form?.Id,
                    Title__c: form?.Title__c,
                    Alias__c: form?.Alias__c,
                    Department__c: form?.Department__c,
                    URL__c: form?.URL__c,
                    Tags__c: form?.Tags__c,
                    Icon__c: form?.Icon__c,
                    Category__c: form?.Category__c,
                    Description__c: form?.Description__c,
                    matchingTagCount: matchingTagCount,
                });
            }
        }

        return relatedForms;
    } catch {
        return [];
    }
};

export const getRelatedForms = (
    selectedForm: FormProps,
    allForms: FormProps[],
) => {
    try {
        let relatedForms: RelatedForm[] = [];
        const selectedFormTags = getTags(selectedForm);

        for (const form of allForms) {
            const formTags = getTags(form);

            const matchingTagCount = getMatchingTagCount(
                formTags,
                selectedFormTags,
            );

            relatedForms = getRelatedFormByMatchingTags(
                relatedForms,
                form,
                selectedForm,
                matchingTagCount,
            );
        }

        return relatedForms;
    } catch {
        return [];
    }
};

export const getFormsBySearchTerm = (
    searchTerm: string,
    allForms: FormProps[],
) => {
    try {
        const allFormsCopy = [...allForms];

        const formSuggestions = allFormsCopy?.filter(
            ({ Title__c, Alias__c, Department__c, Tags__c, Category__c }) =>
                Title__c?.trim()
                    ?.toLowerCase()
                    ?.includes(searchTerm?.trim()?.toLowerCase()) ||
                Alias__c?.trim()
                    ?.toLowerCase()
                    ?.includes(searchTerm?.trim()?.toLowerCase()) ||
                Department__c?.trim()
                    ?.toLowerCase()
                    ?.includes(searchTerm?.trim()?.toLowerCase()) ||
                Tags__c?.trim()
                    ?.toLowerCase()
                    ?.includes(searchTerm?.trim()?.toLowerCase()) ||
                Category__c?.trim()
                    ?.toLowerCase()
                    ?.includes(searchTerm?.trim()?.toLowerCase()),
        );

        return formSuggestions;
    } catch {
        return [];
    }
};

export const getFilterOptions = (items: Category[] | Department[]) => {
    try {
        const filterOptions: DropdownOptions[] = [
            {
                key: 'No Filter',
                text: 'No Filter',
                value: 'No Filter',
            },
        ];

        for (let item of items) {
            filterOptions.push({
                key: item?.name,
                text: item?.name,
                value: item?.name,
            });
        }

        return filterOptions;
    } catch {
        return [
            {
                key: 'No Filter',
                text: 'No Filter',
                value: 'No Filter',
            },
        ];
    }
};

export const getFormsByFilter = (
    categoryFilterSelected: string,
    departmentFilterSelected: string,
    allForms: FormProps[],
) => {
    try {
        if (
            categoryFilterSelected !== 'No Filter' &&
            departmentFilterSelected === 'No Filter'
        ) {
            const filteredForms = allForms?.filter(
                ({ Category__c }) => Category__c === categoryFilterSelected,
            );

            return filteredForms;
        } else if (
            categoryFilterSelected === 'No Filter' &&
            departmentFilterSelected !== 'No Filter'
        ) {
            const filteredForms = allForms?.filter(
                ({ Department__c }) =>
                    Department__c === departmentFilterSelected,
            );

            return filteredForms;
        } else {
            return allForms;
        }
    } catch {
        return [];
    }
};

export const getRecentForms = (
    recentFormIds: string[],
    allForms: FormProps[],
    removeRecentFormId: (uniqueIdentifier: string) => void,
) => {
    try {
        const recentForms: FormProps[] = [];
        for (let formId of recentFormIds) {
            const recentFormIdDetected = allForms?.find(
                ({ Id }) => Id === formId,
            );

            if (recentFormIdDetected) {
                recentForms?.push(recentFormIdDetected);
            } else {
                // A form has been set to internal, inactive or has been deleted from Salesforce
                removeRecentFormId(formId);
            }
        }

        return recentForms;
    } catch {
        return [];
    }
};

export const getFavoritedForms = (
    favoritedFormIds: string[],
    allForms: FormProps[],
    removeFavoritedFormId: (uniqueIdentifier: string) => void,
) => {
    try {
        const favoritedForms: FormProps[] = [];
        for (let formId of favoritedFormIds) {
            const favoritedFormIdDetected = allForms?.find(
                ({ Id }) => Id === formId,
            );

            if (favoritedFormIdDetected) {
                favoritedForms?.push(favoritedFormIdDetected);
            } else {
                // A form has been set to internal, inactive or has been deleted from Salesforce
                removeFavoritedFormId(formId);
            }
        }

        return favoritedForms;
    } catch {
        return [];
    }
};

export const getIsFormFavorited = (
    favoritedFormIds: string[],
    formSelected?: FormProps,
) => {
    const favoritedFormDetected = favoritedFormIds?.find(
        (Id) => Id === formSelected?.Id,
    );

    if (favoritedFormDetected) {
        return true;
    } else {
        return false;
    }
};

export const getSearchTermSuggestionCount = (
    searchTermSuggestions: FormProps[],
) => {
    let searchTermSuggestionDisplayedCount: number = 5;
    const searchTermSuggestionCount: number = searchTermSuggestions?.length;

    if (searchTermSuggestionCount < searchTermSuggestionDisplayedCount) {
        searchTermSuggestionDisplayedCount = searchTermSuggestionCount;
    }

    return {
        searchTermSuggestionCount,
        searchTermSuggestionDisplayedCount,
    };
};
